import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaFacebook, FaInstagram, FaTwitter, FaPinterest } from 'react-icons/fa';
import { cmsAPI, getImageUrl } from '../utils/api';
import '../styles/pages/About.scss';

function About() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    projects: 0,
    clients: 0,
    satisfaction: 0
  });
  const [teamMembers, setTeamMembers] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  // Counter animation
  useEffect(() => {
    const interval = setInterval(() => {
      setStats(prev => ({
        projects: Math.min(prev.projects + 40, 4000),
        clients: Math.min(prev.clients + 30, 3000),
        satisfaction: Math.min(prev.satisfaction + 1, 98)
      }));
    }, 20);

    return () => clearInterval(interval);
  }, []);

  // Fetch Team Members, Testimonials and Gallery
  useEffect(() => {
    cmsAPI.getTeam()
      .then(res => {
        if (res.data?.success && res.data?.data) {
          setTeamMembers(res.data.data);
        }
      })
      .catch(error => console.error('Error fetching team members:', error));

    cmsAPI.getTestimonials()
      .then(res => {
        if (res.data?.success && res.data?.data) {
          setTestimonials(res.data.data);
        }
      })
      .catch(error => console.error('Error fetching testimonials:', error));
  }, []);

  const values = [
    {
      icon: '✓',
      title: 'Integrity',
      description: 'We operate with honesty, transparency, and accountability in every interaction. Trust is the cornerstone of every relationship we build.'
    },
    {
      icon: '🏆',
      title: 'Excellence',
      description: 'We deliver the highest quality in every solution. From the first line of code to final deployment, we exceed standards.'
    },
    {
      icon: '💡',
      title: 'Innovation',
      description: 'We embrace new ideas and emerging technologies to stay ahead of the curve. Innovation drives everything we do.'
    },
    {
      icon: '🤝',
      title: 'Partnership',
      description: 'We build long-term relationships based on mutual respect and shared goals. Our clients are partners in success.'
    }
  ];

  const visibleTeam = teamMembers.slice(0, 3);

  return (
    <div className="about">
      {/* Hero Section */}
      <section className="about-hero" style={{ backgroundImage: "linear-gradient(135deg, rgba(10, 37, 64, 0.85), rgba(0, 82, 106, 0.85)), url('/images/about-bg.jpg')" }}>
        <h1>About Pravara InfoTech</h1>
        <p>Your Partner in Digital Innovation</p>
      </section>

      {/* Story Section */}
      <section id="our-story" className="about-story">
        <div className="story-container">
          <div className="story-image">
            <img src="/images/about-team.jpg" alt="Pravara Infotech Team" />
          </div>
          <div className="story-content">
            <span className="section-tag">Our Story</span>
            <h2>A Leading Technology Company</h2>
            <p>
              Pravara InfoTech is a leading technology company dedicated to transforming businesses through innovative digital solutions.
              Founded in 2018, we set out with a singular vision — to bridge the gap between complex technology and real-world business needs.
            </p>
            <p>
              Our team of skilled professionals brings together deep expertise in software development, cloud computing, UI/UX design, and digital marketing.
              Since our inception, we have partnered with startups, SMEs, and enterprises across diverse industries.
            </p>
            <button className="btn-learn-more" onClick={() => navigate('/about/story')}>Read Full Story →</button>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section id="vision-mission" className="mission-vision">
        <div className="mv-container">
          <h2>What Drives Us</h2>
          <p className="section-desc">Mission & Vision</p>
          <div className="mv-grid">
            <div className="mv-card">
              <span className="mv-number">01</span>
              <h3>Our Mission</h3>
              <p>
                To empower businesses with cutting-edge technology solutions that drive growth, innovation, and digital transformation.
                We are committed to delivering measurable outcomes through strategic consulting and agile development.
              </p>
            </div>
            <div className="mv-card">
              <span className="mv-number">02</span>
              <h3>Our Vision</h3>
              <p>
                To be the most trusted technology partner for businesses worldwide. We envision a future where every organization
                can harness the full power of technology to thrive and succeed.
              </p>
            </div>
          </div>
          <div className="mv-view-details" style={{ marginTop: '45px', textAlign: 'center' }}>
            <button className="btn-learn-more" onClick={() => navigate('/about/vision-mission')}>
              View Detailed Mission & Values &rarr;
            </button>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="core-values">
        <div className="values-container">
          <h2>Our Core Values</h2>
          <p className="section-desc">The foundational principles that shape our culture</p>
          <div className="values-grid">
            {values.map((value, index) => (
              <div key={index} className="value-card">
                <div className="value-icon">{value.icon}</div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="stats-container">
          <div className="stat-card">
            <div className="stat-number">{stats.projects}+</div>
            <div className="stat-label">Projects Completed</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.clients}+</div>
            <div className="stat-label">Happy Clients</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.satisfaction}%</div>
            <div className="stat-label">Client Satisfaction</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">24/7</div>
            <div className="stat-label">Support Available</div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="team-section">
        <div className="team-container">
          <h2>Meet Our Team</h2>
          <p className="section-desc">Talented professionals who bring passion and expertise</p>

          <div className="team-grid">
            {visibleTeam.map((member) => {
              const imageUrl = getImageUrl(member.image, 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200"><rect width="100%" height="100%" fill="%231e293b"/><circle cx="100" cy="80" r="40" fill="%2364748b"/><path d="M30,170 C30,130 70,120 100,120 C130,120 170,130 170,170" fill="%2364748b"/></svg>');

              return (
                <div key={member.id} className="team-circle-card">
                  <div className="team-deco-shapes">
                    <div className="deco-top-right">
                      <span className="deco-pill-1"></span>
                      <span className="deco-pill-2"></span>
                    </div>
                    <div className="deco-bottom-left">
                      <span className="deco-circle"></span>
                      <span className="deco-curve"></span>
                    </div>
                  </div>

                  {/* Circular image */}
                  <div className="team-circle-img">
                    <img src={imageUrl} alt={member.name} />
                  </div>

                  {/* Info */}
                  <div className="team-circle-info">
                    <h3>{member.name}</h3>
                    <span className="team-role">{member.role}</span>
                    <div className="team-social-icons">
                      {member.facebook && <a href={member.facebook} target="_blank" rel="noreferrer" aria-label="Facebook"><FaFacebook /></a>}
                      {member.instagram && <a href={member.instagram} target="_blank" rel="noreferrer" aria-label="Instagram"><FaInstagram /></a>}
                      {member.twitter && <a href={member.twitter} target="_blank" rel="noreferrer" aria-label="Twitter"><FaTwitter /></a>}
                      {member.linkedin && <a href={member.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn"><FaPinterest /></a>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* View More Button */}
          <div className="team-view-more">
            <button className="btn-view-more" onClick={() => navigate('/team')}>
              View More Members
            </button>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section" style={{ padding: '80px 5%', backgroundColor: '#f8f9fa' }}>
        <div className="testimonials-container" style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <span className="section-tag" style={{ color: '#00526A', fontWeight: 'bold', fontSize: '14px', letterSpacing: '2px', textTransform: 'uppercase', display: 'block', marginBottom: '10px', textAlign: 'center' }}>CLIENT FEEDBACK</span>
          <h2 style={{ fontSize: '36px', color: '#0a2540', marginBottom: '20px', textAlign: 'center' }}>What Our Clients Say</h2>
          <p className="section-desc" style={{ color: '#666', fontSize: '18px', maxWidth: '700px', margin: '0 auto 50px', textAlign: 'center' }}>Hear directly from the businesses we have partnered with. Their success stories reflect our commitment to excellence and innovation.</p>
          <div className="testimonials-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '30px' }}>
            {testimonials.map((testimonial, index) => {
              const avatarUrl = getImageUrl(testimonial.image, `https://ui-avatars.com/api/?name=${encodeURIComponent(testimonial.author)}&background=00526A&color=fff&size=40&bold=true`);

              return (
                <div key={index} className="testimonial-card" style={{ backgroundColor: 'white', padding: '40px', borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.05)', position: 'relative' }}>
                  <p className="testimonial-quote" style={{ fontSize: '16px', color: '#555', fontStyle: 'italic', marginBottom: '30px', lineHeight: '1.6' }}>"{testimonial.quote}"</p>
                  <div className="testimonial-author" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <img src={avatarUrl} alt={testimonial.author} className="author-avatar" style={{ width: '50px', height: '50px', borderRadius: '50%', objectFit: 'cover' }} />
                    <div className="author-info">
                      <h4 style={{ margin: '0 0 5px 0', color: '#0a2540', fontSize: '16px' }}>{testimonial.author}</h4>
                      <span style={{ color: '#666', fontSize: '14px' }}>{testimonial.role}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta">
        <h2>Ready to Partner with Us?</h2>
        <p>Let us help you build the technology solutions that will propel your business forward.</p>
        <button className="btn-cta" onClick={() => navigate('/contact')}>Get Started →</button>
      </section>
    </div>
  );
}

export default About;
